<p-toast></p-toast>
<div *ngIf="isLoading" class="flex justify-content-center align-items-center h-full">
    <p-progressSpinner></p-progressSpinner>
</div>

<div *ngIf="!isLoading && promoPlanHeader" class="surface-ground h-full overflow-y-auto p-4">
    <!-- Top Section: Promo Description and Status Tag -->
    <div class="flex justify-content-between align-items-start mb-4">
        <div>
            <h1 class="text-blue-800 text-3xl font-bold">Promo Plan: {{ promoPlanHeader.promoDescription }}</h1>
            <div class="text-white">
                <p-chip [label]="promoPlanHeader.activityType.name" [icon]="promoPlanHeader.activityType.icon"
                    class="bg-purple-300" />
            </div>
        </div>
        <div>
            <p-tag [value]="promoPlanHeader.workflowStatus?.statusName?.toUpperCase()" [severity]="getStatusSeverity()"
                [rounded]="true" class="mr-2"></p-tag>
            <p-button label="Generate Settlement" icon="pi pi-plus" severity="success"
                *ngIf="promoPlanHeader.workflowStatus?.statusName === 'Not Settled'" (onClick)="generateNewSettlement()"
                class="mr-2" [disabled]="!canGenerateSettlement"
                pTooltip="{{!canGenerateSettlement ? 'You are not authorized to generate settlements' : ''}}"></p-button>
            <p-button *ngIf="promoPlanHeader.linkedSessionId" label="Open Session" icon="pi pi-telegram" class="mr-2"
                severity="info" (onClick)="openSession()"></p-button>
            <p-button icon="pi pi-file-excel" label="Export to Excel" styleClass="p-button-help"
                (click)="exportDetailsToExcel()"></p-button>
        </div>
    </div>

    <!-- Header Info Card -->
    <div class="card p-4 mb-4 surface-card border-round-md shadow-2">
        <div class="grid">
            <div class="col-12 md:col-6"><strong>ID:</strong> {{ promoPlanHeader.promoPlanId }}</div>
            <div class="col-12 md:col-6"><strong>Customer:</strong> {{ promoPlanHeader.customer.customerName }}</div>
            <div class="col-12 md:col-6"><strong>Start Date:</strong> {{ promoPlanHeader.promoStartDate | date }}</div>
            <div class="col-12 md:col-6"><strong>End Date:</strong> {{ promoPlanHeader.promoEndDate | date }}</div>
            <div class="col-12 md:col-6"><strong>Channel:</strong> {{ promoPlanHeader.channel }}</div>
            <div class="col-12 md:col-6"><strong>Period:</strong> {{ promoPlanHeader.monthYear }}</div>
            <div class="col-12 md:col-6"><strong>Business:</strong> {{ promoPlanHeader.business?.businessName }}</div>
            <div class="col-12 md:col-6"><strong>Business Unit:</strong> {{
                promoPlanHeader.businessUnit?.businessUnitName }}</div>
            <div class="col-12 md:col-6"><strong>City:</strong> {{ promoPlanHeader.city }}</div>
            <div class="col-12 md:col-6"><strong>Batch#:</strong> {{ promoPlanHeader.batchNumber }}</div>
        </div>
    </div>

    <!-- Horizontal Timeline -->
    <div class="mt-6 mb-4">
        <h3 class="text-lg font-semibold mb-3">Promo Plan Stages</h3>
        <p-timeline [value]="timelineStages" layout="horizontal" styleClass="customized-timeline">
            <ng-template pTemplate="marker" let-item>
                <span
                    class="flex p-3 rounded-full items-center justify-content-center text-white border-circle z-10 shadow-1"
                    [ngClass]="{'bg-blue-500': item.status === currentTimelineStage, 'bg-gray-400': item.status !== currentTimelineStage}">
                    <i [class]="item.icon"></i>
                </span>
            </ng-template>
            <ng-template pTemplate="content" let-item>
                {{ item.status }}
            </ng-template>
        </p-timeline>
    </div>

    <p-divider></p-divider>

    <!-- Tabs for Details, Approvals, Attachments -->
    <p-tabs value="0">
        <p-tablist>
            <p-tab value="0">
                <i class="pi pi-list mr-2"></i>
                <span>Details</span>
            </p-tab>
            <p-tab value="1">
                <i class="pi pi-check-square mr-2"></i>
                <span>Approvals</span>
            </p-tab>
            <p-tab value="2">
                <i class="pi pi-paperclip mr-2"></i>
                <span>Attachments</span>
            </p-tab>
        </p-tablist>
        <p-tabpanels>
            <p-tabpanel value="0">
                <div class="flex justify-content-between align-items-center mb-2">
                    <div class="flex flex-grow-none">
                        <h3 class="text-lg font-semibold">Promo Plan Details</h3>
                    </div>
                    <div class="flex flex-grow-1"></div>
                    <div class="flex flex-grow-none">
                        <div class="flex align-items-center gap-2">
                            <p-inputgroup>
                                <p-inputgroup-addon>
                                    <i class="pi pi-search"></i>
                                </p-inputgroup-addon>
                                <input pInputText type="text" #filter
                                    (input)="dt.filterGlobal(filter.value, 'contains')" placeholder="Search..." />
                            </p-inputgroup>
                        </div>
                    </div>
                </div>
                <p-table #dt [value]="promoPlanDetails" [globalFilterFields]="globalFilterFields" [scrollable]="true"
                    responsiveLayout="scroll" showGridlines="true">
                    <ng-template pTemplate="header">
                        <tr>
                            <th>Actions</th>
                            <th>Status</th>
                            <th>Tamer Item</th>
                            <th>Customer Item</th>
                            <th>Brand</th>
                            <th>Sub Brand</th>
                            <th>Description</th>
                            <th>Discount %age</th>
                            <th>IMS</th>
                            <th>PTT</th>
                            <th>Price After Discount</th>
                            <th>Comp/Sellout</th>
                            <th>Est. Volume</th>
                            <th>Total Comp</th>
                            <th>Comp on LC</th>
                            <th>Line ID</th>
                            <th>Original Line ID</th>
                            <th>Changed Date</th>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="body" let-detail>
                        <tr>
                            <td>
                                <button pButton pRipple icon="pi pi-pencil" class="p-button-rounded p-button-warning"
                                    (click)="editDetail(detail)" *ngIf="(promoPlanHeader.workflowStatus?.statusName === 'Not Settled' 
                                        || promoPlanHeader.workflowStatus?.statusName === 'Draft') && detail.isActive"
                                    [disabled]="!canEditPromoPlanDetail"
                                    pTooltip="{{!canEditPromoPlanDetail ? 'You are not authorized to edit Prom Plan' : ''}}"></button>
                            </td>
                            <td>
                                <p-tag [value]="detail.isActive ? 'Active' : 'Inactive'"
                                    [severity]="detail.isActive ? 'success' : 'danger'"></p-tag>
                            </td>
                            <td>{{ detail.tamerItem?.erpItemNumber ?? '' }} - {{ detail.tamerItem?.erpItemNameE ??
                                'Assorted' }}</td>
                            <td>{{ detail.customerItem?.customerItemDescription ?? 'Assorted' }}</td>
                            <td>{{ detail.brand?.brandName }}</td>
                            <td>{{ detail.subBrand?.subBrandName }}</td>
                            <td>{{ detail.itemDescription }}</td>
                            <td>{{ (detail.discountPercentage * 100) | number }}%</td>
                            <td>{{ detail.ims | number }}</td>
                            <td>{{ detail.ptt | number }}</td>
                            <td>{{ detail.priceAfterDiscount | currency:'SAR' }}</td>
                            <td>{{ detail.compensationPerUnitOfSellout | currency:'SAR' }}</td>
                            <td>{{ detail.estimatedVolume | number }}</td>
                            <td>{{ detail.totalCompensationFixedAmount | currency:'SAR'}}</td>
                            <td>{{ detail.compensationBasedOnLc | currency:'SAR' }}</td>
                            <td>{{ detail.promoPlanLineId }}</td>
                            <td>{{ detail.originalPromoPlanLineId }}</td>
                            <td>{{ detail.changedDate | date:'short' }}</td>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="emptymessage">
                        <tr>
                            <td colspan="18" class="text-center">No promo plan details found.</td>
                        </tr>
                    </ng-template>
                </p-table>
            </p-tabpanel>
            <p-tabpanel value="1">
                <app-approval-card [invokedFromChat]="false" [businessObjectId]="promoPlanHeader?.promoPlanId ?? 0"
                    [businessObjectDescription]="promoPlanHeader?.promoDescription ?? ''"
                    [workflowApprovals]="promoPlanHeader?.workflowApprovals ?? []" [currentUser]="currentUser"
                    [approvalContext]="{ customerId: promoPlanHeader?.customer?.id, businessId: promoPlanHeader?.business?.id, businessUnitId: promoPlanHeader?.businessUnit?.id }"
                    (statusChange)="onStatusChange($event)">
                </app-approval-card>
            </p-tabpanel>
            <p-tabpanel value="2">
                <div class="p-4">
                    <div class="mb-4">
                        <p-fileUpload name="attachments[]" (onSelect)="onFileSelect($event)" [multiple]="true"
                            [accept]="attachmentAcceptTypes" [maxFileSize]="maxFileSize"
                            [disabled]="!canUploadAttachment || isUploadingAttachments"
                            pTooltip="You do not have permission to upload attachments for this item."
                            [tooltipDisabled]="canUploadAttachment" [showUploadButton]="false"
                            [showCancelButton]="false" chooseLabel="Add Attachment" chooseIcon="pi pi-plus"
                            styleClass="p-button-outlined">
                        </p-fileUpload>
                    </div>

                    <div class="mb-3" *ngIf="isUploadingAttachments">
                        <div class="mb-2 text-600 text-sm">Uploading attachments {{ attachmentUploadProgress }}%</div>
                        <p-progressBar [value]="attachmentUploadProgress"></p-progressBar>
                    </div>

                    <p-table [value]="promoPlanHeader.attachments" styleClass="p-datatable-sm" responsiveLayout="scroll"
                        *ngIf="promoPlanHeader.attachments && promoPlanHeader.attachments.length > 0">
                        <ng-template pTemplate="header">
                            <tr>
                                <th>File Name</th>
                                <th>Size</th>
                                <th>Uploaded By</th>
                                <th>Uploaded Date</th>
                                <th style="width: 10rem">Actions</th>
                            </tr>
                        </ng-template>
                        <ng-template pTemplate="body" let-attachment>
                            <tr>
                                <td>
                                    <i class="pi pi-file mr-2"></i>
                                    {{ attachment.fileName }}
                                </td>
                                <td>{{ formatFileSize(attachment.fileSize) }}</td>
                                <td>{{ attachment.uploadedBy || 'N/A' }}</td>
                                <td>{{ attachment.uploadedDate ? (attachment.uploadedDate | appDate:'medium') : 'N/A' }}</td>
                                <td>
                                    <div class="flex gap-2">
                                        <button pButton pRipple icon="pi pi-eye" class="p-button-rounded p-button-info"
                                            (click)="viewAttachment(attachment)" pTooltip="View"
                                            [disabled]="!attachment.attachmentId"></button>
                                        <button pButton pRipple icon="pi pi-download"
                                            class="p-button-rounded p-button-success"
                                            (click)="downloadAttachment(attachment)" pTooltip="Download in new tab"></button>
                                        <button pButton pRipple icon="pi pi-envelope"
                                            class="p-button-rounded p-button-help" (click)="emailAttachment(attachment)"
                                            pTooltip="Email"></button>
                                    </div>
                                </td>
                            </tr>
                        </ng-template>
                    </p-table>

                    <div *ngIf="!promoPlanHeader.attachments || promoPlanHeader.attachments.length === 0"
                        class="text-center text-600 p-3">
                        No attachments found.
                    </div>

                    <div *ngIf="attachmentsChanged" class="flex justify-content-end mt-4">
                        <p-button label="Save Attachment Changes" icon="pi pi-save"
                            (click)="saveAttachments()" [loading]="isLoading || isUploadingAttachments"></p-button>
                    </div>
                </div>
            </p-tabpanel>
        </p-tabpanels>
    </p-tabs>
</div>

<div *ngIf="!isLoading && !promoPlanHeader" class="p-4 text-center">
    <p>Could not load promo plan data. Please check the ID and try again.</p>
</div>

<!-- PDF Viewer Dialog -->
<p-dialog header="View Attachment" [(visible)]="pdfViewerVisible" [modal]="true"
    [style]="{width: '80vw', height: '90vh'}" [maximizable]="true">
    <ng-template pTemplate="content">
        <ngx-extended-pdf-viewer [src]="pdfSrc!" height="85vh"></ngx-extended-pdf-viewer>
    </ng-template>
</p-dialog>

<!-- Image Viewer Dialog -->
<p-dialog header="View Image" [(visible)]="imageViewerVisible" [modal]="true" [style]="{width: '80vw', height: '90vh'}"
    [maximizable]="true">
    <ng-template pTemplate="content">
        <div class="flex justify-content-center align-items-center h-full">
            <img [src]="imageSrc!" alt="Image Attachment"
                style="max-width: 100%; max-height: 85vh; object-fit: contain;">
        </div>
    </ng-template>
</p-dialog>

<!-- Office Document Viewer Dialog (Word, Excel, PowerPoint) -->
<p-dialog header="View Document" [(visible)]="officeViewerVisible" [modal]="true"
    [style]="{width: '80vw', height: '90vh'}" [maximizable]="true">
    <ng-template pTemplate="content">
        <iframe [src]="officeSrc" width="100%" height="100%" frameborder="0"></iframe>
    </ng-template>
</p-dialog>

<!-- EML Viewer Dialog -->
<p-dialog header="View Email" [(visible)]="emlViewerVisible" [modal]="true" [style]="{width: '80vw', height: '90vh'}"
    [maximizable]="true">
    <ng-template pTemplate="content">
        <iframe [src]="emlSrc" width="100%" height="100%" frameborder="0"></iframe>
    </ng-template>
</p-dialog>

<!-- Edit Detail Dialog -->
<p-dialog header="Edit Promo Plan Detail" [(visible)]="displayEditDialog" [modal]="true" [style]="{width: '70vw'}"
    [maximizable]="true">
    <ng-template pTemplate="content">
        <div *ngIf="selectedDetail" class="grid formgrid p-fluid">
            <div class="field col-12 md:col-6">
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-box"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <p-select [options]="filteredItems" [(ngModel)]="selectedDetail.tamerItem" [filter]="true"
                            (onChange)="onTamerItemChange($event)" inputId="tamerItem">
                            <ng-template pTemplate="filter" let-tamerItem>
                                <p-inputgroup>
                                    <p-inputgroup-addon>
                                        <i class="pi pi-search"></i>
                                    </p-inputgroup-addon>
                                    <input #tamerItemInput type="text" pInputText placeholder="Filter"
                                        (keyup)="filterItems(tamerItemInput.value)">
                                    <p-button type="button" icon="pi pi-times"
                                        (click)="tamerItem.reset(); tamerItemInput.value = ''"
                                        styleClass="p-button-secondary"></p-button>
                                </p-inputgroup>
                            </ng-template>
                            <ng-template let-tamerItem pTemplate="item">
                                <span>{{ tamerItem.erpItemNumber }} - {{ tamerItem.erpItemNameE }}</span>
                            </ng-template>
                            <ng-template let-tamerItem pTemplate="selectedItem">
                                <span>{{ tamerItem.erpItemNumber }} - {{ tamerItem.erpItemNameE }}</span>
                            </ng-template>
                        </p-select>
                        <label for="tamerItem">Tamer Item</label>
                    </p-iftalabel>
                </p-inputgroup>
            </div>
            <div class="field col-12 md:col-6">
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-box"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <p-select [options]="filteredCustomerItems" [(ngModel)]="selectedDetail.customerItem"
                            [filter]="true" inputId="customerItem">
                            <ng-template pTemplate="filter" let-customerItem>
                                <p-inputgroup>
                                    <p-inputgroup-addon>
                                        <i class="pi pi-search"></i>
                                    </p-inputgroup-addon>
                                    <input #tamerItemInput type="text" pInputText placeholder="Filter"
                                        (keyup)="customerItem.filter($event)">
                                    <p-button type="button" icon="pi pi-times"
                                        (click)="customerItem.reset(); tamerItemInput.value = ''"
                                        styleClass="p-button-secondary"></p-button>
                                </p-inputgroup>
                            </ng-template>
                            <ng-template let-customerItem pTemplate="item">
                                <span>{{ customerItem.customerItemNumber }} - {{ customerItem.customerItemDescription
                                    }}</span>
                            </ng-template>
                            <ng-template let-customerItem pTemplate="selectedItem">
                                <span>{{ customerItem.customerItemNumber }} - {{ customerItem.customerItemDescription
                                    }}</span>
                            </ng-template>
                        </p-select>
                        <label for="customerItem">Customer Item</label>
                    </p-iftalabel>
                </p-inputgroup>
            </div>
            <div class="field col-12">
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-pencil"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <textarea pInputTextarea [(ngModel)]="selectedDetail.itemDescription" rows="3" class="w-full"
                            inputId="itemDescription"></textarea>
                        <label for="itemDescription">Item Description</label>
                    </p-iftalabel>
                </p-inputgroup>
            </div>
            <div class="field col-12 md:col-6">
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-percentage"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <p-inputNumber [(ngModel)]="selectedDetail.discountPercentage" mode="decimal"
                            [minFractionDigits]="2" [maxFractionDigits]="8"
                            inputId="discountPercentage"></p-inputNumber>
                        <label for="discountPercentage">Discount %</label>
                    </p-iftalabel>
                </p-inputgroup>
            </div>
            <div class="field col-12 md:col-6">
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-box"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <p-inputNumber [(ngModel)]="selectedDetail.ims" inputId="ims"></p-inputNumber>
                        <label for="ims">IMS</label>
                    </p-iftalabel>
                </p-inputgroup>
            </div>
            <div class="field col-12 md:col-6">
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-box"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <p-inputNumber [(ngModel)]="selectedDetail.ptt" inputId="ptt"></p-inputNumber>
                        <label for="ptt">PTT</label>
                    </p-iftalabel>
                </p-inputgroup>
            </div>
            <div class="field col-12 md:col-6">
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-money-bill"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <p-inputNumber [(ngModel)]="selectedDetail.priceAfterDiscount" mode="currency" currency="SAR"
                            inputId="priceAfterDiscount"></p-inputNumber>
                        <label for="priceAfterDiscount">Price After Discount</label>
                    </p-iftalabel>
                </p-inputgroup>
            </div>
            <div class="field col-12 md:col-6">
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-money-bill"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <p-inputNumber [(ngModel)]="selectedDetail.compensationPerUnitOfSellout" mode="currency"
                            currency="SAR" inputId="compensationPerUnitOfSellout"></p-inputNumber>
                        <label for="compensationPerUnitOfSellout">Comp/Sellout</label>
                    </p-iftalabel>
                </p-inputgroup>
            </div>
            <div class="field col-12 md:col-6">
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-chart-bar"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <p-inputNumber [(ngModel)]="selectedDetail.estimatedVolume"
                            inputId="estimatedVolume"></p-inputNumber>
                        <label for="estimatedVolume">Est. Volume</label>
                    </p-iftalabel>
                </p-inputgroup>
            </div>
            <div class="field col-12 md:col-6">
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-money-bill"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <p-inputNumber [(ngModel)]="selectedDetail.totalCompensationFixedAmount" mode="currency"
                            currency="SAR" inputId="totalCompensationFixedAmount"></p-inputNumber>
                        <label for="totalCompensationFixedAmount">Total Comp</label>
                    </p-iftalabel>
                </p-inputgroup>
            </div>
            <div class="field col-12 md:col-6">
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-money-bill"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <p-inputNumber [(ngModel)]="selectedDetail.compensationBasedOnLc" mode="currency" currency="SAR"
                            inputId="compensationBasedOnLc"></p-inputNumber>
                        <label for="compensationBasedOnLc">Comp on LC</label>
                    </p-iftalabel>
                </p-inputgroup>
            </div>
            <div class="field col-12 md:col-6">
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-box"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <p-inputNumber [(ngModel)]="selectedDetail.stockOh" inputId="stockOh"></p-inputNumber>
                        <label for="stockOh">Stock OH</label>
                    </p-iftalabel>
                </p-inputgroup>
            </div>
            <div class="field col-12 md:col-6">
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-money-bill"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <p-inputNumber [(ngModel)]="selectedDetail.currentRsp" mode="currency" currency="SAR"
                            inputId="currentRsp"></p-inputNumber>
                        <label for="currentRsp">Current RSP</label>
                    </p-iftalabel>
                </p-inputgroup>
            </div>
            <div class="field col-12 md:col-6">
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-money-bill"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <p-inputNumber [(ngModel)]="selectedDetail.promoRrp" mode="currency" currency="SAR"
                            inputId="promoRrp"></p-inputNumber>
                        <label for="promoRrp">Promo RRP</label>
                    </p-iftalabel>
                </p-inputgroup>
            </div>
            <div class="field col-12 md:col-6">
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-money-bill"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <p-inputNumber [(ngModel)]="selectedDetail.newCost" mode="currency" currency="SAR"
                            inputId="newCost"></p-inputNumber>
                        <label for="newCost">New Cost</label>
                    </p-iftalabel>
                </p-inputgroup>
            </div>
            <div class="field col-12">
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-comment"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <textarea pInputTextarea [(ngModel)]="selectedDetail.comments" rows="3" class="w-full"
                            inputId="comments"></textarea>
                        <label for="comments">Comments</label>
                    </p-iftalabel>
                </p-inputgroup>
            </div>
        </div>
    </ng-template>
    <ng-template pTemplate="footer">
        <button pButton pRipple label="Cancel" icon="pi pi-times" class="p-button-text" (click)="cancelEdit()"></button>
        <button pButton pRipple label="Save" icon="pi pi-check" (click)="saveDetail()"></button>
    </ng-template>
</p-dialog>
